import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Switch,
  message,
} from "antd";
import { ShippingTypeOption } from "./types";
import { getZippin, listShippingTypes, upsertZippin } from "./api";
import { ExtraFieldsEditor } from "./ExtraFieldsEditor";

interface Props {
  sellerCode: string;
}

interface FormShape {
  enabled: boolean;
  title?: string;
  description?: string;
  shippingTypeIds: number[];
  apiKey?: string;
  apiSecret?: string;
  accountId?: string;
  originId?: string;
  addDeliveryTimeDays?: number;
  extraFields: { label: string; type: string; options?: string }[];
}

export const ZippinCard: React.FC<Props> = ({ sellerCode }) => {
  const [form] = Form.useForm<FormShape>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shippingTypes, setShippingTypes] = useState<ShippingTypeOption[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const [data, types] = await Promise.all([
        getZippin(sellerCode),
        listShippingTypes().catch(() => []),
      ]);
      setShippingTypes(types);
      form.setFieldsValue({
        enabled: data.enabled,
        title: data.title,
        description: data.description,
        shippingTypeIds: data.shippingTypeIds ?? [],
        apiKey: data.apiKey,
        apiSecret: data.apiSecret,
        accountId: data.accountId,
        originId: data.originId,
        addDeliveryTimeDays: data.addDeliveryTimeDays,
        extraFields: data.extraFields ?? [],
      });
    } catch (err: any) {
      message.error("No se pudo cargar Zippin");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sellerCode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await upsertZippin(sellerCode, {
        enabled: values.enabled,
        title: values.title,
        description: values.description,
        shippingTypeIds: (values.shippingTypeIds ?? []).map((v) => Number(v)),
        apiKey: values.apiKey,
        apiSecret: values.apiSecret,
        accountId: values.accountId,
        originId: values.originId,
        addDeliveryTimeDays: values.addDeliveryTimeDays,
        // Magento dynamic_rows legacy data injects `record_id` and `initialize`
        // tracking fields into the persisted JSON. The API DTO uses
        // `forbidNonWhitelisted`, so strip everything that isn't part of
        // the schema before sending.
        extraFields: (values.extraFields ?? []).map(
          ({ label, type, options }: any) => ({ label, type, options }),
        ),
      });
      message.success("Zippin guardado");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Error al guardar";
      message.error(Array.isArray(msg) ? msg.join(" • ") : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Zippin"
      extra={
        <Button type="primary" loading={saving} onClick={handleSave}>
          Guardar
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Habilitado" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={18}>
              <Form.Item
                label="Tipos de carga"
                name="shippingTypeIds"
                tooltip="Tipos de productos que Zippin puede enviar"
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Elegí uno o más tipos"
                  options={shippingTypes.map((t) => ({ value: t.id, label: t.label }))}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="API Key" name="apiKey">
                <Input.Password placeholder="••••••••" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="API Secret" name="apiSecret">
                <Input.Password placeholder="••••••••" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Account ID" name="accountId">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Origin ID" name="originId">
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Días extra al delivery date" name="addDeliveryTimeDays">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <ExtraFieldsEditor name="extraFields" />
        </Form>
      </Spin>
    </Card>
  );
};
